import React from 'react';
import { FileSignature, TrendingUp, TrendingDown, Hourglass, Trophy } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';

/**
 * Board-meeting AO recap panel — embedded in MidYearBoardMeeting +
 * AnnualBoardMeeting. Renders nothing when no tender activity occurred.
 *
 * 02/06/2026 — i18n FR/EN. The qualitative advice voice (next steps for
 * the board) stays FR by design — consistent with Messagerie memos.
 *
 * @param {Object} props
 * @param {Object} props.stats   from buildTenderPeriodStats (or null)
 * @param {'S1'|'année'} [props.scope]
 */
export default function BoardMeetingTenderRecap({ stats, scope = 'S1' }) {
  const { t } = useTranslation();
  if (!stats) return null;
  const { won, lost, total, winRate, securedRevenue, pending, bestClient, viaCounter } = stats;

  // Forward-looking advice tuned to the win rate. FR voice intentional
  // — consistent with Messagerie memos signed by the senior commercial.
  const advice = (() => {
    if (total === 0 && pending > 0) return `Aucune décision tombée sur le ${scope} — ${pending} dossier${pending > 1 ? 's' : ''} encore en instruction côté clients.`; // i18n-keep
    if (winRate === null) return `Pas d'activité commerciale notable sur le ${scope}.`; // i18n-keep
    if (winRate >= 70) return `Excellent ratio. Le conseil félicite la fonction commerciale et encourage une montée en gamme.`; // i18n-keep
    if (winRate >= 40) return `Ratio correct. Le conseil suggère un calibrage prix plus agressif sur 1-2 dossiers ciblés.`; // i18n-keep
    if (winRate >= 20) return `Ratio faible. Le conseil demande à la direction commerciale une revue stratégique des prochaines candidatures.`; // i18n-keep
    return `Ratio insuffisant. Le conseil exige un plan d'action commercial — concentration sur peu de dossiers, mieux préparés.`; // i18n-keep
  })();

  const tone = winRate === null ? 'slate'
    : winRate >= 60 ? 'emerald'
    : winRate >= 35 ? 'sky'
    : winRate >= 15 ? 'amber'
    : 'danger';

  const palette = {
    emerald: { bar: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-300' },
    sky:     { bar: 'bg-sky-500/15 border-sky-500/30',         text: 'text-sky-300' },
    amber:   { bar: 'bg-amber-500/15 border-amber-500/30',     text: 'text-amber-300' },
    danger:  { bar: 'bg-danger/15 border-danger/30',           text: 'text-danger' },
    slate:   { bar: 'bg-slate-700/30 border-slate-500/30',     text: 'text-slate-400' },
  }[tone];

  // Localise the scope label inside the title.
  const localisedScope = scope === 'année' /* i18n-keep — comparator key */
    ? t('board_tender_recap_scope_year')
    : t('board_tender_recap_scope_s1');

  return (
    <div className="bg-ink-700/30 rounded-xl p-4 border border-white/5 space-y-3"
         data-testid="board-tender-recap">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-emerald-300" />
          <p className="text-xs uppercase tracking-widest text-slate-300 font-mono font-bold">
            {t('board_tender_recap_title', { scope: localisedScope })}
          </p>
        </div>
        {winRate !== null && (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono font-bold border ${palette.bar} ${palette.text}`}
                data-testid="board-tender-winrate">
            {t('board_tender_winrate_label', { pct: winRate })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Chip icon={TrendingUp} label={t('board_tender_won_label')} value={won} tone="emerald" testId="board-tender-won" />
        <Chip icon={TrendingDown} label={t('board_tender_lost_label')} value={lost} tone="danger" testId="board-tender-lost" />
        <Chip icon={Hourglass} label={t('board_tender_pending_label')} value={pending} tone="slate" testId="board-tender-pending" />
        <Chip
          icon={Trophy}
          label={t('board_tender_revenue_label')}
          value={securedRevenue > 0 ? formatMoney(securedRevenue) : '—'}
          tone="emerald"
          testId="board-tender-revenue"
          big
        />
      </div>

      {bestClient && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="board-tender-best">
          <Trophy className="w-3 h-3 text-emerald-400" />
          {t('board_tender_top_label')} : <strong className="text-slate-200">{bestClient/* i18n-keep — proper noun */}</strong>
          {viaCounter > 0 && (
            <span className="text-slate-500 italic">
              {' · '}
              {viaCounter > 1
                ? t('board_tender_via_counter_many', { n: viaCounter })
                : t('board_tender_via_counter_one', { n: viaCounter })}
            </span>
          )}
        </p>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed italic border-t border-white/5 pt-2">
        {advice/* i18n-keep — board voice stays FR, consistent with Messagerie memos */}
      </p>
    </div>
  );
}

function Chip({ icon: Icon, label, value, tone, testId, big }) {
  const palette = {
    emerald: 'text-emerald-300',
    danger: 'text-danger',
    slate: 'text-slate-300',
    sky: 'text-sky-300',
  }[tone] || 'text-slate-300';
  return (
    <div className="bg-ink-800/50 rounded p-2 border border-white/5" data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-0.5">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className={`${big ? 'text-sm' : 'text-base'} font-mono font-bold ${palette}`}>
        {value}
      </p>
    </div>
  );
}
